import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const queryClient = useQueryClient();
  const userIdRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next ?? null));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const id = session?.user?.id ?? null;
    if (session !== undefined && userIdRef.current !== id) {
      queryClient.clear();
      userIdRef.current = id;
    }
  }, [session, queryClient]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: "local" });
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo(
    () => ({ session, user: session?.user ?? null, loading: session === undefined, signOut }),
    [session, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
